import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Bug, BookOpen } from "lucide-react";
import { FORMULATOR_DESCRIPTION_MAX, type FormulatorToolsetBridge } from "@ttc-spectree/toolbox-contract";
import { Chip } from "@/components/grooming/grooming-chips";
import { FormulatorNotice } from "@/components/formulator/formulator-notice";
import { PersonaPicker, usePersonaChoice } from "@/components/toolbox/persona-picker";
import { useGroomingProjects } from "@/hooks/queries/use-grooming";
import { canOn, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import { useActiveWorkItemInterviews } from "@/hooks/queries/use-work-item-interview";
import type { GroomingProjectSummary } from "@/lib/api/grooming";
import {
  formulatorSessionPath,
  type InterviewKind,
  type InterviewSessionSummary,
} from "@/lib/api/work-item-interview";
import {
  formulatorErrorMessage,
  formulatorOpeningTurnMessage,
  useFormulatorEvents,
  type FormulatorErrorMessage,
} from "@/lib/toolbox/formulator";
import { projectPath } from "@/lib/grooming/paths";
import { cn } from "@/lib/utils";
import { parseItemInput } from "@/pages/grooming/one-item";
import { TOOLBOX_PAGE_TITLE, TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";

/**
 * The Toolbox Formulator's start view (feature PI-SPEC-SW-1418; design
 * Write view `poStartV`): what the person needs in their own words, its type
 * and project, or an existing item to improve, and the unfinished sessions to
 * pick up. Type and project are chosen explicitly (no silent defaults); only
 * projects with a mapped repository can be chosen, because Claude reads the
 * project's code before it asks anything. Only projects where the person
 * holds the `formulator` capability are listed (epic PI-SPEC-SW-E283).
 */

const KIND_OPTIONS: { value: InterviewKind; label: string; icon: typeof Bug }[] = [
  { value: "story", label: "User story: something new or changed", icon: BookOpen },
  { value: "bug", label: "Bug: something broken", icon: Bug },
];

const NEW_LABELS: Record<InterviewKind, string> = { story: "New user story", bug: "New bug" };

const fieldClass =
  "w-full rounded-[10px] border border-[#3F3F46] bg-[#111113] text-foreground outline-none transition-[border-color,box-shadow] focus:border-[#66A1FF] focus:ring-2 focus:ring-[#66A1FF]/25";

function hasRepos(project: GroomingProjectSummary): boolean {
  return project.repoCount > 0;
}

/** "Waiting for you" etc.: where an unfinished session stands. */
function sessionState(session: InterviewSessionSummary): { text: string; tone: "ok" | "cur" | "none" } {
  if (session.complete) return { text: "Draft complete", tone: "ok" };
  if (session.awaiting === "claude") return { text: "Waiting for Claude", tone: "cur" };
  return { text: "Waiting for you", tone: "none" };
}

function updatedLabel(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function ResumeList() {
  const sessions = useActiveWorkItemInterviews();
  const list = sessions.data ?? [];
  if (list.length === 0) return null;
  return (
    <section className="flex flex-col gap-2" aria-labelledby="formulator-resume" data-testid="formulator-resume">
      <h2 id="formulator-resume" className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        Pick up where you left off
      </h2>
      <div className="glass-card overflow-hidden rounded-[10px]">
        {list.map((session) => {
          const state = sessionState(session);
          return (
            <Link
              key={session.sessionId}
              to={formulatorSessionPath(session.sessionId)}
              className="flex items-center gap-3 border-b border-[#141417] px-4 py-3 last:border-b-0 hover:bg-[#0F0F12]"
              data-testid="formulator-resume-row"
            >
              {session.kind === "bug" ? (
                <Bug className="h-4 w-4 shrink-0 text-red-300" aria-hidden="true" />
              ) : (
                <BookOpen className="h-4 w-4 shrink-0 text-sky-300" aria-hidden="true" />
              )}
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate font-medium">{session.title.trim() || NEW_LABELS[session.kind]}</span>
                <span className="text-xs text-muted-foreground">
                  {session.mode === "improve" && session.itemId ? `Improve #${session.itemId}` : NEW_LABELS[session.kind]}{" "}
                  · {session.container}
                  {updatedLabel(session.updatedAt) !== "" ? ` · ${updatedLabel(session.updatedAt)}` : ""}
                </span>
              </div>
              <Chip tone={state.tone}>{state.text}</Chip>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

/**
 * What another tool may prefill on the start form through router state
 * (`navigate(FORMULATOR_PATH, { state: { prefill } })`): FogBugz to ADO's
 * Feature-case hand-off (epic PI-SPEC-SW-E311, task PI-SPEC-SW-1640-5) opens
 * it with a User Story, the linked ADO project and the case text. Read once,
 * for the first render; nothing starts until the person clicks Start. Never
 * in the URL, so the case text stays out of history and logs.
 */
export interface FormulatorPrefill {
  kind?: InterviewKind;
  project?: string;
  description?: string;
}

/** The `prefill` in router state, when it has the expected shape. Pure; exported for tests. */
export function formulatorPrefillOf(state: unknown): FormulatorPrefill | null {
  if (!state || typeof state !== "object") return null;
  const prefill = (state as { prefill?: unknown }).prefill;
  if (!prefill || typeof prefill !== "object") return null;
  const { kind, project, description } = prefill as Record<string, unknown>;
  return {
    ...(kind === "story" || kind === "bug" ? { kind } : {}),
    ...(typeof project === "string" && project.trim() !== "" ? { project: project.trim() } : {}),
    ...(typeof description === "string" ? { description: description.slice(0, FORMULATOR_DESCRIPTION_MAX) } : {}),
  };
}

export interface FormulatorStartProps {
  formulator: FormulatorToolsetBridge;
  /** The session exists and its opening turn is running; open it. */
  onStarted: (sessionId: string) => void;
}

export function FormulatorStart({ formulator, onStarted }: FormulatorStartProps) {
  const { data: access, isSuccess: accessLoaded } = useToolboxAccess();
  const projectsQuery = useGroomingProjects();
  const preparing = useFormulatorEvents(null);
  const location = useLocation();
  // Read once: a prefill from another tool (FogBugz to ADO's Feature-case hand-off). An unknown project
  // leaves the picker on "Choose a project" (chosenProject below only accepts a listed, mapped project).
  const [prefill] = useState(() => formulatorPrefillOf(location.state));
  const [kind, setKind] = useState<InterviewKind | null>(prefill?.kind ?? null);
  const [project, setProject] = useState(prefill?.project ?? "");
  const [description, setDescription] = useState(prefill?.description ?? "");
  const [itemInput, setItemInput] = useState("");
  const [starting, setStarting] = useState<"create" | "improve" | null>(null);
  const [failure, setFailure] = useState<{ where: "create" | "improve"; message: FormulatorErrorMessage } | null>(null);

  const projects = (projectsQuery.data ?? []).filter((p) => canOn(access, p.project, "formulator"));
  const mapped = projects.filter(hasRepos);
  const unmapped = projects.filter((p) => !hasRepos(p));
  // Grooming projects exist, but none where the person's roles include the Formulator.
  const noRoleHere = accessLoaded && projects.length === 0 && (projectsQuery.data?.length ?? 0) > 0;
  // Case-insensitive, so a prefilled project ("icentral" from a FogBugz link) selects the listed "iCentral".
  const chosenProject = mapped.find((p) => p.project === project || p.project.toLowerCase() === project.toLowerCase())?.project ?? "";
  // E283: "Working as" when the person's roles on the project carry more than one persona.
  const persona = usePersonaChoice(chosenProject || null);
  const canStart = kind !== null && chosenProject !== "" && description.trim() !== "" && starting === null;

  const run = (
    where: "create" | "improve",
    target: string,
    call: () => ReturnType<FormulatorToolsetBridge["start"]>
  ) => {
    setStarting(where);
    setFailure(null);
    // The persona is saved in SpecTree before the interview starts; SpecTree stamps it on the session.
    void persona
      .ensureSaved(target)
      .then(
        () => call(),
        () => {
          setStarting(null);
          return null;
        }
      )
      .then((result) => {
        if (result === null) return;
        if (result.ok) {
          onStarted(result.sessionId);
          return;
        }
        setStarting(null);
        // No answer typed yet: NO_TURN_RETURNED says "first question", not "Your answer is kept".
        setFailure({ where, message: formulatorOpeningTurnMessage(formulatorErrorMessage(result.errorCode, result.message)) });
      })
      .catch(() => {
        setStarting(null);
        setFailure({ where, message: formulatorErrorMessage("UNKNOWN") });
      });
  };

  const start = () => {
    if (!canStart) return;
    run("create", chosenProject, () => formulator.start({ project: chosenProject, kind, description: description.trim() }));
  };

  const improve = () => {
    if (starting !== null) return;
    const parsed = parseItemInput(itemInput);
    if (!parsed) {
      setFailure({
        where: "improve",
        message: { text: "Enter an item ID such as 24606, or paste the item's ADO link.", setup: false },
      });
      return;
    }
    const target = parsed.project ?? chosenProject;
    if (target === "") {
      setFailure({
        where: "improve",
        message: { text: "Choose the project above, or paste the item's ADO link.", setup: false },
      });
      return;
    }
    if (!canOn(access, target, "formulator")) {
      setFailure({
        where: "improve",
        message: {
          text: `You have no Toolbox role on ${target} that includes the ADO Formulator. Ask an Admin or your Project Manager for a role.`,
          setup: false,
        },
      });
      return;
    }
    run("improve", target, () => formulator.start({ project: target, itemId: parsed.id }));
  };

  const projectLabel = chosenProject !== "" ? chosenProject : "the project";
  const startingLine = preparing.activity ?? "Starting the interview";

  return (
    <div className="flex flex-1 justify-center overflow-y-auto px-8 py-8">
      <div className="flex w-full max-w-[640px] flex-col gap-4" data-testid="formulator-start">
        <div className="flex flex-col gap-1.5">
          <h1 className={TOOLBOX_PAGE_TITLE}>What do you need?</h1>
          <p className="text-muted-foreground">
            Say it the way you'd say it to a colleague. Claude will ask what's missing.
          </p>
        </div>

        <div
          className="inline-flex self-start gap-0.5 rounded-[9px] border border-[#1F1F23] bg-[#111113] p-[3px]"
          role="radiogroup"
          aria-label="What kind of item"
        >
          {KIND_OPTIONS.map((option) => {
            const Icon = option.icon;
            const on = kind === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={on}
                data-testid={`formulator-kind-${option.value}`}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-[11px] py-1 text-[13px] font-medium transition-colors",
                  on ? "bg-[#1F1F23] text-foreground" : "text-muted-foreground hover:text-foreground"
                )}
                onClick={() => {
                  setKind(option.value);
                }}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                {option.label}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="formulator-project" className="text-xs font-medium text-muted-foreground">
            Project
          </label>
          <select
            id="formulator-project"
            value={chosenProject}
            onChange={(event) => {
              setProject(event.target.value);
            }}
            className={cn(fieldClass, "h-10 px-3 text-sm font-medium")}
            data-testid="formulator-project"
            disabled={projectsQuery.isPending}
          >
            <option value="" disabled>
              {projectsQuery.isPending ? "Loading projects…" : "Choose a project"}
            </option>
            {projects.map((p) => (
              <option key={p.project} value={p.project} disabled={!hasRepos(p)}>
                {hasRepos(p) ? p.project : `${p.project} (No repository mapped yet)`}
              </option>
            ))}
          </select>
          {projectsQuery.isError && (
            <span className="text-[13px] text-amber-400" role="alert">
              The project list could not be loaded.
            </span>
          )}
          {projectsQuery.isSuccess && noRoleHere && (
            <span className="text-[13px] text-muted-foreground" data-testid="formulator-no-role">
              You have no Toolbox role that includes the ADO Formulator. Ask an Admin or your Project Manager for a role.
            </span>
          )}
          {projectsQuery.isSuccess && mapped.length === 0 && !noRoleHere && (
            <span className="text-[13px] text-muted-foreground" data-testid="formulator-no-projects">
              No project has a repository mapped yet, so Claude has no code to read. A Project Manager or an Admin maps
              one on the Project page's Repositories tab.
            </span>
          )}
          {unmapped.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-muted-foreground" data-testid="formulator-unmapped">
              <span>No repository mapped yet:</span>
              {unmapped.map((p) =>
                canOn(access, p.project, "map-project") ? (
                  <Link
                    key={p.project}
                    to={projectPath(p.project, "repositories")}
                    className="text-link hover:underline"
                    data-testid="formulator-map-repos"
                  >
                    {p.project}: map a repository
                  </Link>
                ) : (
                  <span key={p.project}>{p.project}</span>
                )
              )}
            </div>
          )}
        </div>

        <PersonaPicker choice={persona} />

        <textarea
          value={description}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
          maxLength={FORMULATOR_DESCRIPTION_MAX}
          aria-label="What you need"
          placeholder="For example: customers want to pause all watering for a site from the mobile app when rain is coming."
          className={cn(fieldClass, "min-h-[110px] resize-none px-4 py-3.5 text-[15px] leading-[1.55]")}
          data-testid="formulator-description"
        />
        <p className={cn("text-[13px] leading-relaxed", TOOLBOX_TEXT.faint)}>
          Claude reads {projectLabel}'s code and documentation on this computer to ask better questions. Usually 5 to
          10 minutes. Nothing is created in ADO until you choose to.
        </p>

        {failure?.where === "create" && <FormulatorNotice message={failure.message} testId="formulator-start-error" />}

        <div className="flex items-center gap-3">
          {starting === "create" && <span className="text-xs text-muted-foreground" role="status">{startingLine}</span>}
          <span className="flex-1" />
          <button
            type="button"
            onClick={start}
            disabled={!canStart}
            data-testid="formulator-start-button"
            className="inline-flex h-[30px] items-center gap-1.5 rounded-lg bg-primary px-[13px] text-[13px] font-semibold text-white transition-colors hover:bg-[#C91E38] disabled:pointer-events-none disabled:opacity-40"
          >
            {starting === "create" ? "Starting…" : "Start"}
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>

        <section
          className="glass-card mt-2 flex flex-col gap-2.5 rounded-[10px] px-4 py-3.5"
          aria-labelledby="formulator-improve"
          data-testid="formulator-improve"
        >
          <div className="flex flex-col gap-0.5">
            <h2 id="formulator-improve" className="font-display text-sm font-semibold">
              Improve an existing item
            </h2>
            <span className="text-[13px] text-muted-foreground">
              Claude asks only about what the item is missing, then you review the changes before ADO is updated.
            </span>
          </div>
          <div className="flex items-center gap-2.5">
            <input
              value={itemInput}
              onChange={(event) => {
                setItemInput(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") improve();
              }}
              placeholder="24606 or https://dev.azure.com/…"
              aria-label="Item ID or ADO link"
              className={cn(fieldClass, "h-10 px-3.5 font-mono text-[13px]")}
              data-testid="formulator-improve-input"
            />
            <button
              type="button"
              onClick={improve}
              disabled={itemInput.trim() === "" || starting !== null}
              data-testid="formulator-improve-button"
              className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-lg border border-[#27272A] bg-[#1A1A1E] px-3 text-[13px] font-medium transition-colors hover:bg-[#222227] disabled:pointer-events-none disabled:opacity-40"
            >
              {starting === "improve" ? "Starting…" : "Improve"}
            </button>
          </div>
          {starting === "improve" && <span className="text-xs text-muted-foreground" role="status">{startingLine}</span>}
          {failure?.where === "improve" && <FormulatorNotice message={failure.message} testId="formulator-improve-error" />}
        </section>

        <ResumeList />
      </div>
    </div>
  );
}
